import {ILoginCredentials, ILoginWithFormOptions, IUserModel} from "../user-model.interface";
import {IServiceFactory} from "../../../service-factory.interface";
import {IDotRezUserSession} from "../../../dot-rez-api/session/user-session/dot-rez-user.session.interface";
import {DotRezUserSession} from "../../../dot-rez-api/session/user-session/dot-rez-user.session";
import {IReactionDisposer, makeObservable, observable, runInAction} from "mobx";
import {IDotRezBookingSession} from "../../../dot-rez-api/session/booking-session/dot-rez-booking.session.interface";
import {LoginAndRegistrationHandler} from "../login-and-registration-handler/login-and-registration-handler";
import {AnonymousUserProfileModel} from "./profile/anonymous-user-profile.model";
import {LoginAndRegistrationDialogTabEnum} from "../login-and-registration-dialog-tab.enum";
import {Check} from "../../../../types/type-checking";
import {IDialogConfirmationHandler} from "../../../dialog/dialog.service.interface";
import {AuthorizedUserModel} from "../authorized-user/authorized-user.model";
import {NullableString} from "../../../../types/nullable-types";
import {UserRefreshTokenStorage} from "../../user-refresh-token-storage";
import {AirlineWebapiErrorCodesEnum} from "../../../airline-webapi/airline-webapi-error-codes.enum";
import {ILoginResponse} from "../../../airline-webapi/responses/login.response";
import {ICreateBookingSessionOptions, ICreateFlightSearchSessionOptions} from "../../user.service.interface";


export class AnonymousUserModel implements IUserModel {
    constructor(public readonly services: IServiceFactory) {
        this._getSessionPromise = this._createUserSession();
        makeObservable<this, '_getSessionPromise'>(this, {
            _getSessionPromise: observable.ref
        });

        this.profile = new AnonymousUserProfileModel(this);
    }

    private _reactions: IReactionDisposer[] = [];

    get isAuthorized(): boolean {
        return false;
    }

    get refreshToken(): NullableString {
        return null;
    }

    get rememberMe(): boolean {
        return false;
    }

    readonly profile: AnonymousUserProfileModel;

    private _getSessionPromise: Promise<IDotRezUserSession>;
    getSession(): Promise<IDotRezUserSession> {
        return this._getSessionPromise;
    }


    private _registrationInstructionsDialogHandler: IDialogConfirmationHandler | null = null;

    registrationConfirmed(): void {
        if(this._registrationInstructionsDialogHandler) {
            this._registrationInstructionsDialogHandler.accept();
        }
    }

    async loginWithCredentials(credentials: ILoginCredentials): Promise<IUserModel> {
        try {
            const loginResponse = await this.services.airlineWebapi.login({
                userName: credentials.userName,
                password: credentials.password,
                domain: credentials.domain
            });

            let loginResponseData: ILoginResponse | null;
            let password = credentials.password;


            if(loginResponse.hasError) {
                if(loginResponse.errorCode === AirlineWebapiErrorCodesEnum.PasswordExpired) {
                    const changeExpiredPasswordResult = await this.services.user.changeExpiredPassword({
                        userName: credentials.userName,
                        oldPassword: credentials.password,
                        domain: credentials.domain
                    });

                    if(!changeExpiredPasswordResult) {
                        return this;
                    }

                    loginResponseData = changeExpiredPasswordResult.loginResponseData;
                    password = changeExpiredPasswordResult.newPassword;

                } else {
                    this.services.alert.showError(this.services.language.translateApiError(loginResponse.errorCode));
                    return this;
                }
            } else {
                loginResponseData = loginResponse.data;
            }

            this.services.analytics.logLoginEvent();

            const userModel = new AuthorizedUserModel(this.services, {
                refreshTokenStorage: new UserRefreshTokenStorage(credentials.rememberMe, loginResponseData.refreshToken, this.services),
                dotRezToken: loginResponseData.dotRezToken
            });

            await this.services.booking.upgradeCurrentBookingSessionToAuthorizedUser(credentials.userName, password, credentials.domain, this.profile.userNameHash);

            return userModel;

        } catch (err) {
            this.services.logger.error(`Login failed for user ${credentials.userName}`, err);
            this.services.alert.showError(this.services.language.genericApiErrorMessage());
            return this;
        }

    }

    async loginWithForm(options: ILoginWithFormOptions): Promise<IUserModel> {
        const loginAndRegistrationHandler = new LoginAndRegistrationHandler(this.services);

        await this.services.dialogFactory.showLoginAndRegistration({
            defaultTab: options.defaultTab,
            hideRegistrationTab: Check.isNullOrUndefined(options.hideRegistrationTab) ? false : options.hideRegistrationTab,
            loginAndRegistrationHandler: loginAndRegistrationHandler
        });

        if(loginAndRegistrationHandler.isRegistrationSuccessful) {
            await this.services.dialogFactory.showRegistrationInstructions({
                captureDialogHandler: dialogHandler => this._registrationInstructionsDialogHandler = dialogHandler
            });

            this._registrationInstructionsDialogHandler = null;
            return await this.loginWithForm({
                hideRegistrationTab: true,
                defaultTab: LoginAndRegistrationDialogTabEnum.Login
            });
        } else if(loginAndRegistrationHandler.authorizedUser) {
            const loginForm = loginAndRegistrationHandler.loginFormFields;

            await this.services.booking.upgradeCurrentBookingSessionToAuthorizedUser(loginForm.userName.value!, loginAndRegistrationHandler.password, loginForm.domain.value!, this.profile.userNameHash);

            return loginAndRegistrationHandler.authorizedUser;
        }

        return this;

    }

    async logout(): Promise<IUserModel> {
        return this;
    }


    private async _createDotRezToken(options: ICreateBookingSessionOptions): Promise<string> {
        if(options.forDog) {
            return await this.services.dotRezApi.createDogToken();
        } else {
            return await this.services.dotRezApi.createAnonymousToken();
        }

    }

    private async _createUserSession(): Promise<IDotRezUserSession> {
        const token = await this._createDotRezToken({
            forDog: false
        });
        return new DotRezUserSession(token, this.services);
    }

    reCreateSession(): Promise<IDotRezUserSession> {
        const promise = this._createUserSession();
        runInAction(() => {
            this._getSessionPromise = promise;
        });

        return promise;
    }

    async createBookingSession(options: ICreateBookingSessionOptions): Promise<IDotRezBookingSession> {
        const token = await this._createDotRezToken(options);
        return this.services.dotRezApi.createBookingSession(token);
    }

    async createFlightSearchSession(options: ICreateFlightSearchSessionOptions): Promise<IDotRezBookingSession> {
        let token: string = await this._createDotRezToken({
            forDog: options.forDog
        });

        return this.services.dotRezApi.createFlightSearchSession(token);
    }

    async createTransientBookingSession(): Promise<IDotRezBookingSession> {
        const token = await this._createDotRezToken({
            forDog: false
        });
        return this.services.dotRezApi.createTransientBookingSession(token);
    }

    dispose(): void {
        this._reactions.forEach(r => r());
        this._reactions = [];
    }

    async changePassword(): Promise<ILoginResponse | null> {
        throw new Error('Not supported');
    }
}
